import React, { useState, useEffect } from "react";
import { loadStripe } from "@stripe/stripe-js";
import {
  Button,
  Card,
  CardBody,
  CardHeader,
  Col,
  Row,
  Spinner,
} from "react-bootstrap";
import {
  Elements,
  CardElement,
  PaymentElement,
  useStripe,
  useElements,
  CardNumberElement,
  CardExpiryElement,
  CardCvcElement,
} from "@stripe/react-stripe-js";

import {
  createSubscription,
  createPaymentintent,
  saveSubscriptionInfo,
  cancelSubscription,
  checkPaymentDate,
} from "../../../api/subscription";
import { auth, db } from "../../../firebaseConfig";
import { collection, updateDoc, setDoc, doc } from "firebase/firestore";
import { APIClient } from "../../../api/apiCore";
import { processPayment } from "../../../api/subscription";
import { useSnackbar } from "../../../utils/snackbar";

interface AuthUser {
  paymentDate: Date;
  nextPaymentDate: Date;
  subscriptionId: string;
}

const CheckoutForm: React.FC = () => {
  const showSnackbar = useSnackbar();

  const stripe = useStripe();
  const elements = useElements();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [isProcessing, setIsProcessing] = useState(false);
  const [isPaid, setIsPaid] = useState(false);
  const [userData, setUserData] = useState<AuthUser>();
  const [cardElementOptions, setCartElementOptions] = useState({
    style: {
      base: {
        height: "30px !important",
        borderColor: "#fff !important",
        fontSize: "16px",
        color: "#424770",
        "::placeholder": {
          color: "#aab7c4",
        },
      },
      invalid: {
        color: "#9e2146",
      },
    },
    disabled: false,
    showIcon: true,
  });

  useEffect(() => {
    const handleCheckPaymentDate = async () => {
      try {
        const response: any = await checkPaymentDate();

        if (response.valid == true) {
          setIsPaid(true);
        }
      } catch (error) {
        // setIsPaid(false);
      }
    };
    handleCheckPaymentDate();
  }, []);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!stripe || !elements) {
      setErrorMessage("Stripe has not loaded yet.");
      return;
    }

    setIsProcessing(true);

    const cardNumberElement = elements.getElement(CardNumberElement);
    if (!cardNumberElement) {
      setErrorMessage("Card element not found.");
      setIsProcessing(false);
      return;
    }

    // Step 1: Create a payment method
    const { error, paymentMethod } = await stripe.createPaymentMethod({
      type: "card",
      card: cardNumberElement,
    });

    if (error) {
      setErrorMessage(error.message || "Payment method creation failed.");
      setIsProcessing(false);
      return;
    }

    console.log("Payment Method Created:", paymentMethod);

    const response: any = await createSubscription({
      paymentMethodId: paymentMethod.id,
    });

    const subscriptionData = response;

    if (subscriptionData.error) {
      setErrorMessage(subscriptionData.error);
      setIsProcessing(false);
      return;
    }

    // Step 3: Confirm subscription if required
    const { subscription, clientSecret } = subscriptionData;
    if (clientSecret) {
      const { error: confirmError, paymentIntent } =
        await stripe.confirmCardPayment(clientSecret);

      if (confirmError) {
        setErrorMessage(
          confirmError.message || "Subscription confirmation failed.",
        );
      } else if (paymentIntent?.status === "succeeded") {
      }
    } else {
      try {
        const response: any = await saveSubscriptionInfo();
        setIsPaid(true);
        showSnackbar(response.message, "info");
      } catch (error: any) {
        showSnackbar(error.message, "error");
        setIsProcessing(false);
      }
    }

    setIsProcessing(false);
  };

  const handleCancelSubscription = async () => {
    try {
      setIsProcessing(true);
      const response = await cancelSubscription();
      showSnackbar(response.message, "info");
      setIsProcessing(false);
    } catch (error: any) {
      showSnackbar(error.message, "error");
      setIsProcessing(false);
    }
  };

  return (
    <>
      {isPaid ? (
        <>
          <div
            style={{
              border: "1px solid rgb(230, 241, 243)",
              backgroundColor: "rgb(230, 241, 243)",
              padding: "5px",
            }}
          >
            You have paid for this month
          </div>
          <Button
            style={{ width: "100%" }}
            className="mt-3"
            onClick={handleCancelSubscription}
            disabled={isProcessing}
          >
            Cancel Subscription
            {isProcessing && (
              <Spinner animation="border" size="sm" className="ms-2" />
            )}
          </Button>
        </>
      ) : (
        <form onSubmit={handleSubmit}>
          <label>Card Number</label>
          <div
            style={{
              marginBottom: "10px",
              padding: "5px",
              border: "1px solid #ced4da",
              borderRadius: "5px",
            }}
          >
            <CardNumberElement options={cardElementOptions} />
          </div>

          <label>Expiry Date</label>
          <div
            style={{
              marginBottom: "10px",
              padding: "5px",
              border: "1px solid #ced4da",
              borderRadius: "5px",
            }}
          >
            <CardExpiryElement options={cardElementOptions} />
          </div>

          <label>CVC</label>
          <div
            style={{
              marginBottom: "10px",
              padding: "5px",
              border: "1px solid #ced4da",
              borderRadius: "5px",
            }}
          >
            <CardCvcElement options={cardElementOptions} />
          </div>

          <Button
            type="submit"
            disabled={!stripe || isProcessing}
            style={{ width: "100%" }}
            className="mt-3"
          >
            Checkout
            {isProcessing && (
              <Spinner animation="border" size="sm" className="ms-2" />
            )}
          </Button>
          {errorMessage && <div>{errorMessage}</div>}
        </form>
      )}
    </>
  );
};

const Subscription = () => {
  const [clientSecret, setClientSecret] = useState<string | null>(null);

  const [tempstripeInstance, setStripeInstance] = useState<any>(null);

  useEffect(() => {
    const loadStripeLibrary = async () => {
      console.log(process.env.REACT_APP_STRIPE_SECRET_KEY);
      const stripeInstance = loadStripe(
        process.env.REACT_APP_STRIPE_SECRET_KEY!,
      );
      setStripeInstance(stripeInstance);
    };

    loadStripeLibrary();
  }, []);
  return (
    <div className="right-panel">
      <div className="sticky-top bg-white shadow-sm w-100 p-3 d-flex justify-content-between align-items-center z-index-100">
        <span
          className="mb-0 default-text"
          style={{
            fontWeight: "500",
            paddingTop: "11px",
            paddingBottom: "11px",
          }}
        >
          Subscription
        </span>
      </div>

      <div className="py-3  px-4">
        <div id="breadcrumb" className="d-flex gap-2 my-3">
          <i className="bx bx-home font-size-22"></i>
          <svg
            xmlns="http://www.w3.org/2000/svg"
            width="24"
            height="24"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
            className="flex-shrink-0 h-5 w-5"
            aria-hidden="true"
          >
            <polyline points="9 18 15 12 9 6"></polyline>
          </svg>
          <span>
            <a style={{ fontWeight: "500" }}> Subscription</a>
          </span>
        </div>
        <div className="pt-3">
          <Row
            style={{ display: "flex", alignItems: "stretch" }}
            className="g-3"
          >
            <Col lg={6}>
              <Card style={{ height: "100%" }}>
                <CardBody>
                  <Elements stripe={tempstripeInstance}>
                    <CheckoutForm />
                  </Elements>
                </CardBody>
              </Card>
            </Col>
            <Col lg={6}>
              <div
                style={{ backgroundColor: "#e6f1f3" }}
                className="px-4 h-100"
              >
                <h4
                  style={{ color: "rgb(0 145 234)", textAlign: "center" }}
                  className="pt-4"
                >
                  Pro Plan Includes
                </h4>
                <ul className="subscription-custom-check ">
                  <li>Unlimited Clients & Invoices & Quotes</li>
                  <li>Send Invoice Emails via Gmail or MSN Accounts</li>
                  <li>Customize Invoice Designs & Email Templates</li>
                </ul>
              </div>
            </Col>
          </Row>
        </div>
      </div>
    </div>
  );
};

export default Subscription;
